// CandidatePage.tsx
import React from "react";
import { useParams, useNavigate } from "react-router-dom";

type CandidateApiResp = {
  ok: boolean;
  error?: string;
  candidate?: {
    fullName?: string;
    role?: string;
    publicToken?: string;
    paymentStatus?: string;
    issuedAt?: string | null;
    expiresAt?: string | null;
    // API may provide linkState but we compute it if missing
    linkState?: "paid" | "expired" | "active";
    amountINR?: string | number;
    amountUSD?: string | number;
    paypalLink?: string;
  };
};

function formatIsoShort(iso?: string | null) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function remainingSeconds(iso?: string | null) {
  if (!iso) return 0;
  const t = Date.parse(iso);
  if (isNaN(t)) return 0;
  return Math.max(0, Math.floor((t - Date.now()) / 1000));
}

export default function CandidatePage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  // <<---- PUT YOUR DEPLOYED APPSCRIPT EXEC URL HERE ---->>
  const SCRIPT_URL = "https://script.google.com/macros/s/AKfycbxTr9OLdDE33W2Xq7vkdFcOBMMCC79ZIYbVebwrrgfEJ05Vj_CFT8nnqlHiPbknVJ_JjQ/exec";

  const [loading, setLoading] = React.useState(true);
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  const [candidate, setCandidate] = React.useState<CandidateApiResp["candidate"] | null>(null);
  const [secondsLeft, setSecondsLeft] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (!token) {
      setErrorMsg("Missing token in URL.");
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchCandidate() {
      try {
        setLoading(true);
        setErrorMsg(null);

        const url = new URL(SCRIPT_URL);
        url.searchParams.set("token", token);

        const res = await fetch(url.toString(), { cache: "no-store" });
        if (!res.ok) {
          const t = await res.text().catch(() => "");
          throw new Error(`Network error: ${res.status} ${res.statusText} ${t}`);
        }

        const data: CandidateApiResp = await res.json();

        if (cancelled) return;

        if (!data.ok) {
          setErrorMsg(data.error || "Token not found or server error.");
          setLoading(false);
          return;
        }

        if (!data.candidate) {
          setErrorMsg("Candidate record missing.");
          setLoading(false);
          return;
        }

        // normalize candidate object
        const c = { ...data.candidate };

        // Normalize payment status: treat blank as "Pending"
        const rawStatus = (c.paymentStatus || "").toString().trim();
        const paymentStatus = rawStatus ? rawStatus.toLowerCase() : "pending";

        // determine expiresAt (API may return or issuedAt exists)
        let expiresIso: string | null = c.expiresAt ?? null;
        if (!expiresIso && c.issuedAt) {
          try {
            const d = new Date(c.issuedAt);
            if (!isNaN(d.getTime())) {
              d.setHours(d.getHours() + 2); // 2-hour window (same as server)
              expiresIso = d.toISOString();
            }
          } catch {
            expiresIso = null;
          }
        }

        // compute linkState if API didn't provide it
        let linkState: "paid" | "expired" | "active" = (c.linkState as any) ?? "active";

        const now = new Date();
        const isExpired = expiresIso ? now.getTime() > new Date(expiresIso).getTime() : false;

        if (paymentStatus === "yes" || paymentStatus === "paid") {
          linkState = "paid";
        } else if (paymentStatus === "no" || paymentStatus === "n") {
          // If explicitly "No" treat as expired if expiry passed, else treat as expired (business rule)
          linkState = isExpired ? "expired" : "expired";
        } else {
          // pending / blank
          linkState = isExpired ? "expired" : "active";
        }

        // store normalized fields
        c.expiresAt = expiresIso;
        c.paymentStatus = paymentStatus;
        c.linkState = linkState;

        setCandidate(c);
        setLoading(false);

        // if active -> redirect to payment page and pass candidate info in state (preserve your original behaviour)
        if (linkState === "active") {
          // use amounts from sheet if present, fallback to defaults
          const amountINR = c.amountINR ? Number(c.amountINR) : 1150;
          const amountUSD = c.amountUSD ? Number(c.amountUSD) : 15;
          navigate("/payment", {
            replace: true,
            state: {
              name: c.fullName ?? "",
              role: c.role ?? "",
              amountINR,
              amountUSD,
              paypalLink: c.paypalLink ?? undefined,
              token: token,
              expiresAt: c.expiresAt ?? null,
            },
          });
          return;
        }

        // For paid/expired show UI; initialize countdown if expiresAt present
        if (c.expiresAt) {
          const secs = remainingSeconds(c.expiresAt);
          setSecondsLeft(secs);
        } else {
          setSecondsLeft(null);
        }
      } catch (err: any) {
        console.error("fetchCandidate error:", err);
        if (!cancelled) {
          setErrorMsg(err.message || "Unknown error");
          setLoading(false);
        }
      }
    }

    fetchCandidate();
    return () => {
      cancelled = true;
    };
  }, [token, navigate, SCRIPT_URL]);

  // Countdown ticker (if secondsLeft set)
  React.useEffect(() => {
    if (secondsLeft === null) return;
    const id = setInterval(() => {
      setSecondsLeft((s) => (s && s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [secondsLeft]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-[#eef6fb]">
        <div className="text-center">
          <div className="mb-4 animate-pulse text-[#0A66C2] font-semibold text-lg">Preparing your secure payment page…</div>
          <div className="text-sm text-[#0b1320]/70">Verifying link & payment status. This only takes a moment.</div>
        </div>
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-[#eef6fb] p-4">
        <div className="max-w-3xl w-full rounded-2xl bg-white shadow-md p-8 text-center border border-[#e6eef6]">
          <h2 className="text-2xl font-semibold text-red-600">Link error</h2>
          <p className="mt-4 text-sm text-[#0b1320]/70">{errorMsg}</p>
          <div className="mt-6 text-sm">
            Contact: <a className="text-[#0A66C2] underline" href="mailto:technocollabs@gmail.com">technocollabs@gmail.com</a> or <a className="text-[#0A66C2] underline" href="tel:+918319291391">+91 8319291391</a>
          </div>
        </div>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-[#eef6fb] p-4">
        <div className="text-[#0b1320]/70">No candidate data available.</div>
      </div>
    );
  }

  const state = candidate.linkState;

  // ---- PAID screen ----
  if (state === "paid") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-[#f4fbf9] flex items-start justify-center py-12 px-6">
        <div className="max-w-5xl w-full space-y-6">
          <div className="rounded-2xl bg-white border border-[#def6e9] shadow-md p-8 flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="flex-shrink-0">
              <div className="w-20 h-20 rounded-lg bg-green-50 flex items-center justify-center border border-green-200">
                {/* check icon */}
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M20 6L9 17l-5-5" stroke="#047857" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-2xl font-extrabold text-[#065f46]">Payment Received</h1>
              <p className="mt-2 text-[#064e3b]/80">Thank you — we have received your internship registration payment.</p>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="p-3 bg-[#f0fdfa] rounded-md border border-[#d1fae5]">
                  <div className="text-xs text-[#065f46]/80">Candidate</div>
                  <div className="font-medium text-[#065f46]">{candidate.fullName}</div>
                </div>
                <div className="p-3 bg-[#f0fdfa] rounded-md border border-[#d1fae5]">
                  <div className="text-xs text-[#065f46]/80">Role</div>
                  <div className="font-medium text-[#065f46]">{candidate.role}</div>
                </div>
                <div className="p-3 bg-[#f0fdfa] rounded-md border border-[#d1fae5]">
                  <div className="text-xs text-[#065f46]/80">Status</div>
                  <div className="font-medium text-[#065f46]">Paid</div>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-3 items-center">
                <a href="/" className="px-4 py-2 rounded-md bg-[#0A66C2] text-white text-sm shadow-sm">Return to site</a>
                <a href={`mailto:contact@technocolabs.com?subject=Payment confirmation for ${encodeURIComponent(candidate.fullName || "")}`} className="px-4 py-2 rounded-md border border-[#e6eef6] text-sm">Email support</a>
              
              </div>
            </div>
          </div>

          {/* Informational panels */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-lg p-4 bg-white border border-[#e6eef6] shadow-sm">
              <h3 className="font-semibold">What happens next</h3>
              <p className="mt-2 text-sm text-[#0b1320]/70">We will verify your payment and send an offer letter within 1–2 hours same day.</p>
            </div>
            <div className="rounded-lg p-4 bg-white border border-[#e6eef6] shadow-sm">
              <h3 className="font-semibold">Receipt</h3>
              <p className="mt-2 text-sm text-[#0b1320]/70">If you need an official receipt, reply to the confirmation email or contact support.</p>
            </div>
            <div className="rounded-lg p-4 bg-white border border-[#e6eef6] shadow-sm">
              <h3 className="font-semibold">Support</h3>
              <p className="mt-2 text-sm text-[#0b1320]/70">
                Email: <a className="text-[#0A66C2] underline" href="mailto:technocollabs@gmail.com">technocollabs@gmail.com</a><br/>
                Phone: <a className="text-[#0A66C2] underline" href="tel:+918319291391">+91 8319291391</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- EXPIRED screen ----
  if (state === "expired") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-[#f0f6f9] flex items-center justify-center py-20 px-6">
        <div className="max-w-4xl w-full rounded-2xl bg-gradient-to-r from-[#0b3247] to-[#0a2540] text-white p-6 md:p-10 shadow-lg relative overflow-hidden">
          {/* watermark - hidden on small screens to avoid overlap */}
          <div className="hidden md:block absolute right-6 bottom-6 text-[40px] font-extrabold text-red-600 pointer-events-none select-none opacity-12">
            EXPIRED
          </div>

          <div className="relative z-10">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <div>
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-lg bg-white/10 flex items-center justify-center border border-white/6">
                  {/* X icon */}
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path d="M18 6L6 18M6 6l12 12" stroke="#ed0f0fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl font-extrabold">Payment link expired</h1>
                <p className="mt-2 text-white/80">We are sorry! the payment window for this internship link has expired. As you passed the deadline to pay the Enrollment fees.</p>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 bg-white/6 rounded-md">
                    <div className="text-xs text-white/80">Candidate</div>
                    <div className="font-medium">{candidate.fullName}</div>
                    <div className="text-xs text-white/70 mt-1">Role: {candidate.role}</div>
                     <div className="text-xs text-white/70 mt-1">Status: Offer Terminated</div>
                  </div>

                  <div className="p-4 bg-white/6 rounded-md">
                    <div className="text-xs text-white/80">What to do</div>
                    <div className="mt-1 text-sm text-white/80">
                      Please contact the team to request assistance or a new payment link.
                    </div>
                    <div className="mt-3 flex flex-wrap gap-3">
                      <a className="px-3 py-2 rounded-md bg-white text-[#0a2540] text-sm font-medium" href="mailto:technocollabs@gmail.com">
                        Email Support
                      </a>
                      <a className="px-3 py-2 rounded-md border border-white/20 text-white text-sm" href="tel:+918319291391">
                        Call +91 8319291391
                      </a>
                    </div>
                  </div>
                </div>

               
              </div>
            </div>

            <div className="mt-6 text-sm text-white/80">
              If you believe this is an error, contact support and include your name and role so we can assist.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // fallback (shouldn't happen)
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-[#eef6fb] p-6">
      <div className="max-w-lg w-full rounded-lg bg-white shadow p-6 text-center">
        <div className="text-lg font-semibold">Preparing your secure payment page…</div>
        <div className="mt-3 text-sm text-[#0b1320]/70">If you're not redirected, <button className="text-[#0A66C2] underline" onClick={() => navigate("/payment", { state: { name: candidate?.fullName ?? "", role: candidate?.role ?? "", amountINR: candidate?.amountINR ?? 1150, amountUSD: candidate?.amountUSD ?? 15, token } })}>click here</button>.</div>
      </div>
    </div>
  );
}
